import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { IconArrowLeft, IconMail } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/api/auth.api';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    mutationFn: authApi.forgotPassword,
    onSuccess: () => setSent(true),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/vynzo-logo.svg" alt="Vynzo" className="h-12 w-12" />
          <h1 className="font-display text-2xl font-extrabold text-text-primary">Reset your password</h1>
        </div>

        <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-8 shadow-xl">
          {sent ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-state-success-subtle">
                <IconMail className="h-6 w-6 text-state-success" />
              </div>
              <p className="text-sm text-text-secondary">
                If an account with that email exists, a password reset link has been sent.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="flex flex-col gap-5">
              <p className="text-sm text-text-secondary">Enter your email and we'll send you a link to reset your password.</p>
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@vynzo.app"
                error={errors.email?.message}
                {...register('email')}
              />
              <Button type="submit" loading={mutation.isPending} fullWidth>
                Send reset link
              </Button>
            </form>
          )}

          <Link to="/login" className="mt-6 flex items-center justify-center gap-1.5 text-xs font-medium text-text-link hover:underline">
            <IconArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
